import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { API_URL, authFetch, parseJsonResponse, responseErrorMessage } from "../utils/auth";
import ToolSidebar from "../utils/ToolSideBar";
import "../../css/partner/Meetings.css";

const hours = Array.from({ length: 11 }, (_, index) => index + 8);
const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const toDateInput = (date) => date.toISOString().slice(0, 10);
const startOfWeek = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() - next.getDay());
  return next;
};
const addDays = (date, count) => {
  const next = new Date(date);
  next.setDate(next.getDate() + count);
  return next;
};
const formatHour = (hour) => new Date(2026, 0, 1, hour).toLocaleTimeString([], { hour: "numeric" });
const minutesFromTime = (time) => {
  const [hour, minute] = String(time || "00:00").split(":").map(Number);
  return hour * 60 + minute;
};

export default function Meetings({ partner }) {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [settings, setSettings] = useState({ timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York" });
  const [windows, setWindows] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      try {
        const [meetingsRes, settingsRes, windowsRes, overridesRes] = await Promise.all([
          authFetch(`${API_URL}/meetings/?bucket=`),
          authFetch(`${API_URL}/meetings/settings/`),
          authFetch(`${API_URL}/meetings/weekly-availability/`),
          authFetch(`${API_URL}/meetings/exceptions/`),
        ]);
        const meetingsData = await parseJsonResponse(meetingsRes);
        const settingsData = await parseJsonResponse(settingsRes);
        const windowsData = await parseJsonResponse(windowsRes);
        const overridesData = await parseJsonResponse(overridesRes);
        if (!meetingsRes.ok) throw new Error(responseErrorMessage(meetingsRes, meetingsData, "Unable to load meetings."));
        if (settingsRes.ok) setSettings(settingsData);
        if (windowsRes.ok) setWindows(windowsData?.results || []);
        if (overridesRes.ok) setOverrides(overridesData?.results || []);
        setMeetings(meetingsData?.results || []);
      } catch (err) {
        setError(err.message || "Unable to load meetings.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [weekStart]);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)), [weekStart]);
  const weekLabel = `${weekDays[0].toLocaleDateString([], { month: "short", day: "numeric" })} - ${weekDays[6].toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}`;

  const weekMeetings = meetings.filter((meeting) => {
    const date = new Date(meeting.start_at);
    return date >= weekDays[0] && date < addDays(weekDays[6], 1);
  });
  const pendingMeetings = meetings.filter((meeting) => meeting.status === "pending" || meeting.requires_action);
  const needsResponseMeetings = meetings.filter((meeting) => meeting.requires_action);
  const upcomingMeetings = meetings.filter((meeting) => new Date(meeting.start_at) >= new Date() && ["pending", "confirmed", "reschedule_requested"].includes(meeting.status)).slice(0, 5);

  function availabilityState(day, hour) {
    const dateKey = toDateInput(day);
    const override = overrides.find((item) => item.date === dateKey);
    if (override?.is_unavailable) return "blocked";
    const weekday = (day.getDay() + 6) % 7;
    const cellStart = hour * 60;
    const cellEnd = cellStart + 60;
    const source = override && !override.is_unavailable ? [override] : windows.filter((item) => Number(item.weekday) === weekday && item.is_active);
    return source.some((item) => minutesFromTime(item.start_time) < cellEnd && minutesFromTime(item.end_time) > cellStart) ? "available" : "unavailable";
  }

  function meetingsForCell(day, hour) {
    return weekMeetings.filter((meeting) => {
      const start = new Date(meeting.start_at);
      return start.toDateString() === day.toDateString() && start.getHours() === hour;
    });
  }

  function requestAt(day, hour) {
    const start = new Date(day);
    start.setHours(hour, 0, 0, 0);
    navigate(`/meetings/new?start_at=${encodeURIComponent(start.toISOString())}`);
  }

  return (
    <div className="staff-shell">
      <ToolSidebar active="meetings" isPartner={partner} />
      <main className="staff-page meetings-page calendar-first-page">
        <header className="meeting-hero">
          <div>
            <span>Scheduling</span>
            <h1>Meetings</h1>
            <p>Your weekly calendar in {settings.timezone || "your saved time zone"}.</p>
          </div>
          <div className="meeting-actions">
            <Link to="/settings/availability" className="btn btn-secondary">Edit Availability</Link>
            <Link to="/meetings/new" className="btn btn-primary">New Meeting</Link>
          </div>
        </header>

        <section className="calendar-toolbar staff-card">
          <div className="calendar-nav">
            <button type="button" onClick={() => setWeekStart(addDays(weekStart, -7))}>Previous Week</button>
            <button type="button" onClick={() => setWeekStart(startOfWeek(new Date()))}>Today</button>
            <button type="button" onClick={() => setWeekStart(addDays(weekStart, 7))}>Next Week</button>
          </div>
          <strong>{weekLabel}</strong>
        </section>

        {error && <p className="meeting-error">{error}</p>}

        <section className="meeting-summary-grid">
          <article className="staff-card summary-card"><span>Upcoming</span><strong>{upcomingMeetings.length}</strong></article>
          <article className="staff-card summary-card"><span>Needs Response</span><strong>{needsResponseMeetings.length}</strong></article>
          <article className="staff-card summary-card"><span>Time Zone</span><strong>{settings.timezone || "Not set"}</strong></article>
        </section>

        <section className="calendar-legend" aria-label="Calendar legend">
          <span><i className="available" /> Available</span>
          <span><i className="unavailable" /> Unavailable</span>
          <span><i className="confirmed" /> Confirmed meeting</span>
          <span><i className="pending" /> Pending request</span>
        </section>

        {loading ? <section className="weekly-calendar staff-card calendar-loading-card" aria-live="polite"><div className="availability-loading calendar-loading" role="status"><span className="meeting-spinner" aria-hidden="true" /><strong>Loading calendar...</strong><small>Checking availability and meeting requests.</small></div></section> : (
          <section className="weekly-calendar staff-card">
            <div className="calendar-grid calendar-heading-row">
              <div className="time-heading" />
              {weekDays.map((day) => <div className={`day-heading ${day.toDateString() === new Date().toDateString() ? "today" : ""}`} key={day.toISOString()}><strong>{dayNames[day.getDay()]}</strong><span>{day.toLocaleDateString([], { month: "short", day: "numeric" })}</span></div>)}
            </div>
            <div className="calendar-body">
              {hours.map((hour) => (
                <div className="calendar-grid calendar-hour-row" key={hour}>
                  <div className="hour-label">{formatHour(hour)}</div>
                  {weekDays.map((day) => {
                    const state = availabilityState(day, hour);
                    const cellMeetings = meetingsForCell(day, hour);
                    const isBookable = state === "available";
                    return <div className={`calendar-cell ${state} ${isBookable ? "bookable" : ""}`} key={`${day.toISOString()}-${hour}`}><button type="button" className="calendar-cell-action" disabled={!isBookable} onClick={() => isBookable && requestAt(day, hour)}><span className="cell-state">{state === "available" ? "Available - request" : state === "blocked" ? "Blocked" : "Unavailable"}</span></button>{cellMeetings.map((meeting) => <Link to={`/meetings/${meeting.id}`} className={`calendar-event ${meeting.status}`} key={meeting.id}><strong>{meeting.title}</strong><small>{meeting.status_label}</small></Link>)}</div>;
                  })}
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="meeting-agenda-grid">
          <div className="staff-card meeting-list-panel"><h2>Upcoming Meetings</h2>{upcomingMeetings.length === 0 ? <p className="compact-empty">No upcoming meetings.</p> : upcomingMeetings.map((meeting) => <Link to={`/meetings/${meeting.id}`} className="agenda-row" key={meeting.id}><strong>{meeting.title}</strong><span>{new Date(meeting.start_at).toLocaleString()}</span></Link>)}</div>
          <div className="staff-card meeting-list-panel"><h2>Pending Requests</h2>{pendingMeetings.length === 0 ? <p className="compact-empty">No pending approvals.</p> : pendingMeetings.map((meeting) => <Link to={`/meetings/${meeting.id}`} className="agenda-row" key={meeting.id}><strong>{meeting.title}</strong><span>{meeting.requires_action ? "Action needed" : meeting.status_label}</span></Link>)}</div>
        </section>
      </main>
    </div>
  );
}
